import { ComparatorService } from '@adgate/common/services/comparator-service';
import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { forkJoin, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommercialType } from '@adgate/common/types/commercial-type.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Category } from '@adgate/common/types/category.type';
import { BaseObject } from '@adgate/common/types/base-object.type';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { Agency } from '@adgate/common/types/agency.type';
import { Language } from '@adgate/common/types/language.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { User } from '@adgate/modules/admin/types/user.type';
import { FileSelector } from "@adgate/common/components/file-selector/file-selector";
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { AsperaFileSelector } from '@adgate/common/components/aspera-file-selector/aspera-file-selector';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';


@Component({
  selector: 'app-tv-commercial-edit-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatAutocompleteModule,
    ObjectDescPipe,
    FileSelector,
    AsperaFileSelector
],
  templateUrl: './tv-commercial-edit-dialog-component.html',
  styleUrl: './tv-commercial-edit-dialog-component.scss',
})
export class TvCommercialEditDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<TvCommercialEditDialogComponent> = inject(MatDialogRef<TvCommercialEditDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private authService: AuthenticationService = inject(AuthenticationService);
  private dialogService: DialogService = inject(DialogService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);

  public comparatorService: ComparatorService = inject(ComparatorService);

  commercialForm: FormGroup;

  commercialTypes: CommercialType[] = [];
  categories: Category[] = [];
  commercialOffices: CommercialOffice[] = [];
  filteredCommercialOffices: CommercialOffice[] = [];
  agencies: Agency[] = [];
  filteredAgencies: Agency[] = [];
  brands: string[] = [];
  filteredBrands!: Observable<string[]>;
  products: string[] = [];
  filteredProducts!: Observable<string[]>;
  languages: Language[] = [];
  commercialAttributes: BaseObject[] = [];
  uploadMethods: BaseObject[] = [ { id: 0, description: "Upload Later" }, { id: 1, description: "HTTP" }, { id: 2, description: "Aspera Connect" } ];

  constructor() 
  {
    this.commercialForm = this.fb.group({
      commercialType: ['', Validators.required],
      category: ['', Validators.required],
      commercialOffice: ['', Validators.required],
      agency: ['', Validators.required],
      brandAdvertiser: ['', Validators.required],
      product: ['', Validators.required],
      referenceTitle: ['', Validators.required],
      crt: [{value: '', disabled: true}, [Validators.required, Validators.pattern(/^[0-9]*$/), Validators.maxLength(15)]],
      language: ['', Validators.required],
      commercialAttributes: [[]],
      duration: ['', [Validators.required, Validators.min(1)]],
      uploadMethod: ['', Validators.required],
      file: [{value: '', disabled: true}, [Validators.required, CustomValidators.filenameValidator()]],
      media: [null]
    });
  }

  ngOnInit(): void 
  {
    let userAttributes = this.authService.getUserAttributes();
    let officeFilter = null;
    let agencyFilter = null;

    if( userAttributes.type == User.TYPE_OFFICE ) {
      agencyFilter = userAttributes.office.id;
    } else if ( userAttributes.type == User.TYPE_AGENCY ) {
      officeFilter = userAttributes.agency.id;
    }

    forkJoin([
      this.commonObjectService.getCommercialTypes(CommercialType.USAGE_TV),
      this.commonObjectService.getCategories(),
      this.commonObjectService.getCommercialAttributes(),
      this.commonObjectService.getLanguages(),
      this.commonObjectService.getCommercialOffices(officeFilter),
      this.commonObjectService.getAgencies(agencyFilter)

    ]).subscribe({
      next: (responses:RESTApplicationResponse[]) => {
        this.commercialTypes = responses[0].data;
        this.categories = responses[1].data;
        this.commercialAttributes = responses[2].data;
        this.languages = responses[3].data;
        this.commercialOffices = responses[4].data;
        this.agencies = responses[5].data;

        this.initializeUserProfile(userAttributes);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  initializeUserProfile(userAttributes: any): void
  {
    if (userAttributes.type == User.TYPE_OFFICE) {
      this.commercialForm.get('commercialOffice')?.setValue(userAttributes.office);
      this.commercialForm.get('commercialOffice')?.disable();

    } else if (userAttributes.type == User.TYPE_AGENCY) {
      this.commercialForm.get('agency')?.setValue(userAttributes.agency);
      this.commercialForm.get('agency')?.disable();
      this.onChangeAgency(userAttributes.agency);
    }
  }

  onChangeCommercialOffice(office: BaseObject): void
  {
    this.commonObjectService.getAgencies(office.id).subscribe({
      next: (response: RESTApplicationResponse) => {
        this.agencies = response.data;
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }
    
  onChangeAgency(agency: BaseObject): void
  {
    forkJoin([
      this.commonObjectService.getBrands(agency.id),
      this.commonObjectService.getProducts(agency.id)

    ]).subscribe({
      next: (responses:RESTApplicationResponse[]) => {
        this.brands = responses[0].data;
        this.products = responses[1].data;

        this.filteredBrands = this.commercialForm.get('brandAdvertiser')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterBrands(value || ''))
        );

        this.filteredProducts = this.commercialForm.get('product')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterProducts(value || ''))
        );
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onChangeLanguage(language: Language): void 
  {
    if( language.id == Language.PORTUGUESE ) {
      this.commercialForm.get('crt')?.enable();
    } else {
      this.commercialForm.get('crt')?.disable();
      this.commercialForm.get('crt')?.setValue('');
    }
  }

  onChangeUploadMethod( method: BaseObject ): void
  {
    if( method.id != AdGateConstants.UPLOADMETHOD_NOUPLOAD ) {
      this.commercialForm.get('file')?.enable();
    } else {
      this.commercialForm.get('file')?.disable();
    }
  }

  onSelectFile(selectedFiles: File[]): void
  {
    if( selectedFiles != null && selectedFiles.length > 0 ) {
      this.commercialForm.get('media')?.setValue(selectedFiles[0]);
    }
  }

  onSelectFileAspera(selectedFiles: any): void
  {
    if( selectedFiles != null && selectedFiles.dataTransfer.files.length > 0 ) {
      this.commercialForm.get('media')?.setValue(selectedFiles.dataTransfer);
    }
  }

  onCancel(): void 
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void
  {
    if (this.commercialForm.valid) {
      let c: Commercial = new Commercial();
      c.type = this.commercialForm.get('commercialType')?.value;
      c.category = this.commercialForm.get('category')?.value;
      c.commercialOffice = this.commercialForm.get('commercialOffice')?.value;
      c.agency = this.commercialForm.get('agency')?.value;
      c.brand = this.commercialForm.get('brandAdvertiser')?.value;
      c.product = this.commercialForm.get('product')?.value;
      c.version = this.commercialForm.get('referenceTitle')?.value;
      c.language = this.commercialForm.get('language')?.value;
      c.crt = this.commercialForm.get('crt')?.value || null;
      c.attributes = this.commercialForm.get('commercialAttributes')?.value || [];
      c.durationEstimated = this.commercialForm.get('duration')?.value;

      let r: TvCommercialEditDialogResult = {
        commercial: c,
        uploadMethod: this.commercialForm.get('uploadMethod')?.value,
        media: this.commercialForm.get('media')?.value
      }

      this.dialogRef.close(r);
    }
  }

  private _filterBrands(value: string): string[] 
  {
    const filterValue = value.toLowerCase();
    return this.brands.filter(brand => brand.toLowerCase().includes(filterValue));
  }

  private _filterProducts(value: string): string[] 
  {
    const filterValue = value.toLowerCase();
    return this.products.filter(product => product.toLowerCase().includes(filterValue));
  }
}

export interface TvCommercialEditDialogResult
{
  commercial: Commercial;
  uploadMethod: BaseObject;
  media: any;
}